import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Button from "@mui/material/Button";
import {
  buscarCabeceraPorId,
  getAtributo,
} from "../context/FuncionesContext";
import VistaContext from "../Vista/context/VistaContext";
import Label from "../ABM/componentes/inputs/LabelF";
import {
  startCameraScanner,
  waitForVideoElement,
} from "../../../helpers/scanner";
import { entregarValorAInputDestino } from "../../../helpers/inputDestino";

const isMobileDevice = () => {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }

  const userAgent = navigator.userAgent || navigator.vendor || window.opera || "";

  return (
    /Android|iPhone|iPad|iPod|IEMobile|Opera Mini/i.test(userAgent) ||
    window.matchMedia?.("(max-width: 900px)").matches
  );
};

const buscarCabeceraPorDestino = (cabeceras, inputDestino) => {
  if (!inputDestino) return null;

  const encontrada = buscarCabeceraPorId(cabeceras, inputDestino);
  if (encontrada) return encontrada;

  const stack = Array.isArray(cabeceras) ? [...cabeceras] : [];
  while (stack.length > 0) {
    const current = stack.shift();
    if (!current) continue;

    if (
      current.clave_envio === inputDestino ||
      current.campo_alias === inputDestino
    ) {
      return current;
    }

    if (Array.isArray(current.sc_hijos) && current.sc_hijos.length > 0) {
      stack.push(...current.sc_hijos);
    }
  }

  return null;
};

const InputCam = (props) => {
  const { setValor, cab, data, context, id_elemento } = props;
  const isABM = typeof setValor === "function";
  const pantallaContext = useContext(context ?? VistaContext) ?? {};
  const cabeceras = pantallaContext.cabeceras ?? [];

  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [scannerError, setScannerError] = useState("");
  const [isProcessingScan, setIsProcessingScan] = useState(false);
  const buttonRef = useRef(null);
  const videoRef = useRef(null);
  const scannerStreamRef = useRef(null);
  const scannerControlsRef = useRef(null);
  const scannerSessionRef = useRef(0);
  const scannerLockRef = useRef(false);

  const inputDestino =
    getAtributo("input_destino", data, cab) ??
    getAtributo("destino", data, cab);
  const visibleEnPc =
    (getAtributo("visible_en_pc", data, cab) ??
      getAtributo("visible_pc", data, cab) ??
      "n") === "s";
  const formatosConfigurados = getAtributo("formatos", data, cab);
  const formatos = formatosConfigurados;

  const destinoCab = useMemo(
    () => buscarCabeceraPorDestino(cabeceras, inputDestino),
    [cabeceras, inputDestino],
  );

  const mobile = isMobileDevice();
  const supportsCamera =
    typeof navigator !== "undefined" &&
    Boolean(navigator.mediaDevices?.getUserMedia);

  useEffect(() => {
    // Fast Refresh puede conservar el estado anterior del componente.
    // Al montar o recargar, el escáner siempre debe comenzar cerrado.
    scannerSessionRef.current += 1;
    setIsScannerOpen(false);
    setIsProcessingScan(false);

    return () => {
      scannerSessionRef.current += 1;

      if (scannerStreamRef.current) {
        scannerStreamRef.current.getTracks().forEach((track) => track.stop());
      }

      if (scannerControlsRef.current?.stop) {
        scannerControlsRef.current.stop();
      }
    };
  }, []);

  const stopScanner = () => {
    scannerSessionRef.current += 1;

    if (scannerStreamRef.current) {
      scannerStreamRef.current.getTracks().forEach((track) => track.stop());
      scannerStreamRef.current = null;
    }

    if (scannerControlsRef.current?.stop) {
      scannerControlsRef.current.stop();
    }
    scannerControlsRef.current = null;

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    scannerLockRef.current = false;
    setIsScannerOpen(false);
    setIsProcessingScan(false);
  };

  const entregarValor = (value) => {
    if (!destinoCab) {
      setScannerError(
        `No se encontro la configuracion del campo destino ${inputDestino}.`,
      );
      return false;
    }

    const entregadoAlInput = entregarValorAInputDestino({
      origen: buttonRef.current,
      destino: inputDestino,
      valor: value,
    });

    if (!entregadoAlInput) {
      setScannerError(
        `No se encontro un input compatible para el destino ${inputDestino}.`,
      );
      return false;
    }

    return true;
  };

  const applyScannedValue = async (rawValue) => {
    // Mantener exactamente el mismo valor que entrega input_escaner.
    const value = String(rawValue ?? "").trim();

    if (!value || !inputDestino) {
      return;
    }

    setScannerError("");
    entregarValor(value);
  };

  const handleDetectedCode = async (rawValue) => {
    if (!rawValue || scannerLockRef.current) return;

    scannerLockRef.current = true;
    await applyScannedValue(rawValue);
    stopScanner();
  };

  const openScanner = async (event) => {
    const nativeEvent = event && event.nativeEvent;

    // La cámara sólo puede abrirse por una interacción real sobre este botón.
    // Bloquea llamadas programáticas a click() o a openScanner().
    if (!nativeEvent || nativeEvent.isTrusted !== true) {
      return;
    }

    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    setScannerError("");

    if (!inputDestino || (!mobile && !visibleEnPc)) {
      return;
    }

    if (!supportsCamera) {
      setScannerError(
        window.isSecureContext
          ? "El navegador no permite acceder a la camara en vivo."
          : "La camara en vivo requiere abrir la aplicacion mediante HTTPS.",
      );
      return;
    }

    const session = scannerSessionRef.current + 1;
    scannerSessionRef.current = session;
    scannerLockRef.current = false;
    setIsScannerOpen(true);
    setIsProcessingScan(true);

    try {
      const video = await waitForVideoElement(videoRef);
      if (scannerSessionRef.current !== session) return;

      const controls = await startCameraScanner("multi-format", {
        video,
        onDetected: handleDetectedCode,
        formatos,
      });

      if (scannerSessionRef.current !== session) {
        controls.stop();
        return;
      }

      scannerControlsRef.current = controls;
      scannerStreamRef.current = controls.getStream?.() ?? null;

      setIsProcessingScan(false);
    } catch (error) {
      setScannerError(
        "No se pudo iniciar el lector de codigos. Revisa el permiso de camara.",
      );
      stopScanner();
    }
  };

  if (!inputDestino || (!mobile && !visibleEnPc)) {
    return null;
  }

  const disabled =
    data?.[cab.id_a + "_disabled"]
      ? data[cab.id_a + "_disabled"] === "s"
      : cab.disabled === "s";

  return (
    <>
      {isABM ? (
        cab.nombre ? (
          <Label
            label={cab.nombre}
            opcionales_null={cab.opcionales_null}
            permite_null={cab.permite_null}
          />
        ) : null
      ) : cab.nombre ? (
        <div className="vista_label">
          <p
            style={{
              maxHeight: "25px",
              position: "relative",
              top: "2px",
              wordBreak: "break-all",
            }}
            className="suW"
          >
            {cab.nombre}:
          </p>
        </div>
      ) : null}
      <Button
        ref={buttonRef}
        id={id_elemento}
        type="button"
        variant="contained"
        onClick={openScanner}
        className={cab.input_class_caja}
        style={{
          width: cab.input_size || cab.width_input || cab.width || "100%",
          minWidth: cab.input_size || cab.min_width,
          marginBottom: cab.margin_bottom_abm,
        }}
        disabled={disabled}
      >
        {cab.label || cab.placeholder || "Escanear con camara"}
      </Button>
      {scannerError ? (
        <div
          style={{
            marginTop: "6px",
            color: "#b42318",
            fontSize: "0.8rem",
          }}
        >
          {scannerError}
        </div>
      ) : null}
      {isScannerOpen ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.85)",
            zIndex: 1600,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            padding: "16px",
            gap: "12px",
          }}
        >
          <video
            ref={videoRef}
            muted
            playsInline
            autoPlay
            style={{
              width: "100%",
              maxWidth: "420px",
              borderRadius: "16px",
              background: "#000",
            }}
          />
          <div
            style={{
              color: "#fff",
              textAlign: "center",
              fontSize: "0.95rem",
            }}
          >
            {isProcessingScan
              ? "Abriendo camara..."
              : "Apunta la camara al codigo"}
          </div>
          <Button
            variant="contained"
            onClick={stopScanner}
            style={{
              background: "#fff",
              color: "#111",
            }}
          >
            Cancelar
          </Button>
        </div>
      ) : null}
    </>
  );
};

export default InputCam;
